import React, { useState, useContext } from "react";
import { Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";

import { getToken } from "../../../../api/Account";
import { DataContext } from "../../../../context/DataContext";
import { tienePermiso } from "../../../../helpers/permisos";
import { EstadoChip, useAviso } from "../../../../components/base";
import { formatearMonto } from "../../../../helpers/formato";
import {
  calcularAguinaldoByEmployeeAsync,
  deleteAguinaldoAsync,
  getAguinaldosByEmployeeAsync,
  payAguinaldoAsync,
} from "../../../../api/Employees";
import MediumModal from "../../../../components/modals/MediumModal";
import PagoAguinaldoDetails from "../aguinaldos/PagoAguinaldoDetails";
import HistorialTab from "./HistorialTab";
import {
  celdaFecha,
  celdaMonto,
  periodo,
  useConfirmacion,
  useEscritura,
  useListaRemota,
} from "../comunes";

const EstadoAguinaldo = ({ pagado }) =>
  pagado ? (
    <EstadoChip estado="ok" label="Pagado" />
  ) : (
    <EstadoChip estado="aviso" label="Pendiente" />
  );

const Aguinaldo = ({ id }) => {
  const [selectedAguinaldo, setSelectedAguinaldo] = useState([]);
  const [showPrintModal, setShowPrintModal] = useState(false);

  const token = getToken();
  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { pedir, dialogo } = useConfirmacion();

  const {
    datos: histoyAguinaldo,
    cargando,
    error,
    reload: localReload,
    setReload: setLocalReload,
  } = useListaRemota(() => getAguinaldosByEmployeeAsync(token, id));

  const payAguinaldo = async (aguinaldoId) => {
    const ok = await escribir(
      () => payAguinaldoAsync(token, aguinaldoId),
      "No se pudo pagar el aguinaldo"
    );
    if (!ok) return;
    setLocalReload(!localReload);
    aviso.exito("Aguinaldo pagado");
  };

  const calcularAguinaldo = async () => {
    const ok = await escribir(
      () => calcularAguinaldoByEmployeeAsync(token, id),
      "No se pudo calcular el aguinaldo"
    );
    if (!ok) return;
    setLocalReload(!localReload);
    aviso.exito("Aguinaldo calculado");
  };

  const deleteAguinaldo = async (aguinaldoId) => {
    const ok = await escribir(
      () => deleteAguinaldoAsync(token, aguinaldoId),
      "No se pudo eliminar el aguinaldo"
    );
    if (!ok) return;
    setLocalReload(!localReload);
    aviso.exito("Aguinaldo eliminado");
  };

  const descripcionDe = (row) =>
    `aguinaldo #${row.id}${row.description ? ` «${row.description}»` : ""} de ${formatearMonto(
      row.monto
    )} (${periodo(row.desde, row.hasta)})`;

  const pedirCalcular = () =>
    pedir({
      titulo: "Calcular aguinaldo",
      mensaje: (
        <Typography component="p">
          Se calculará el aguinaldo de este empleado y quedará registrado como pendiente de pago.
        </Typography>
      ),
      textoConfirmar: "Calcular aguinaldo",
      onConfirmar: calcularAguinaldo,
    });

  const pedirPagar = (row) =>
    pedir({
      titulo: "Pagar aguinaldo",
      mensaje: (
        <Typography component="p">
          {`Se registrará el pago del ${descripcionDe(row)}.`}
        </Typography>
      ),
      textoConfirmar: "Pagar aguinaldo",
      onConfirmar: () => payAguinaldo(row.id),
    });

  const pedirEliminar = (row) =>
    pedir({
      titulo: "Eliminar aguinaldo",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`Se eliminará el ${descripcionDe(row)}.`}
          </Typography>
          <Typography component="p" sx={{ color: "text.secondary" }}>
            Esta acción no se puede deshacer.
          </Typography>
        </>
      ),
      textoConfirmar: "Eliminar aguinaldo",
      peligro: true,
      onConfirmar: () => deleteAguinaldo(row.id),
    });

  // Calcular el aguinaldo es CalcularAguinaldoByEmployee: el servidor solo pide NOMINA VER aunque
  // escribe un registro, así que en pantalla se pide NOMINA CREAR (quien puede ver sin crear no lo ofrece).
  const puedeAgregar = tienePermiso(access, "NOMINA CREAR");
  const puedeEditar = tienePermiso(access, "NOMINA UPDATE");
  // La pantalla pedía NOMINA DELETE y DeleteAguinaldo exige NOMINA UPDATE en el servidor: se piden los dos.
  const puedeEliminar =
    tienePermiso(access, "NOMINA DELETE") && tienePermiso(access, "NOMINA UPDATE");

  const columnas = [
    {
      id: "id",
      etiqueta: "#",
      desde: "md",
      sx: { fontVariantNumeric: "tabular-nums" },
      render: (row) => row.id,
    },
    {
      id: "descripcion",
      etiqueta: "Descripción",
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {row.description}
        </Typography>
      ),
    },
    {
      id: "periodo",
      etiqueta: "Período",
      desde: "sm",
      sx: celdaFecha,
      render: (row) => periodo(row.desde, row.hasta),
    },
    {
      id: "monto",
      etiqueta: "Monto",
      alinear: "right",
      sx: celdaMonto,
      render: (row) => (
        <Typography variant="monto" component="span">
          {formatearMonto(row.monto)}
        </Typography>
      ),
    },
    {
      id: "estado",
      etiqueta: "Estado",
      desde: "sm",
      render: (row) => <EstadoAguinaldo pagado={row.isPayed} />,
    },
  ];

  return (
    <div>
      <HistorialTab
        titulo="Aguinaldos"
        descripcion="Aguinaldo calculado del empleado y su pago."
        etiquetaTabla="Historial de aguinaldos"
        agregar={
          puedeAgregar
            ? {
                etiqueta: "Calcular aguinaldo",
                icono: <CalculateOutlinedIcon />,
                onClick: pedirCalcular,
              }
            : undefined
        }
        cargando={cargando}
        error={error}
        onReintentar={() => setLocalReload(!localReload)}
        etiquetaCarga="Cargando aguinaldos…"
        filas={histoyAguinaldo}
        columnas={columnas}
        detalleMovil={(row) => (
          <>
            <Typography variant="caption" component="span" sx={{ display: "block", mb: 0.5 }}>
              {periodo(row.desde, row.hasta)}
            </Typography>
            <EstadoAguinaldo pagado={row.isPayed} />
          </>
        )}
        etiquetaFila={(row) => `aguinaldo #${row.id}`}
        vacio={{
          titulo: "Sin aguinaldos",
          descripcion: "Calcula el aguinaldo del empleado para que aparezca aquí.",
        }}
        acciones={(row) => [
          // Pagar mientras no se pague y se pueda; si no, el comprobante (como siempre).
          !row.isPayed && puedeEditar
            ? {
                id: "pagar",
                etiqueta: "Pagar",
                icono: <PaidOutlinedIcon />,
                onClick: () => pedirPagar(row),
              }
            : {
                id: "imprimir",
                etiqueta: "Imprimir comprobante",
                icono: <PrintOutlinedIcon />,
                onClick: () => {
                  setSelectedAguinaldo(row);
                  setShowPrintModal(true);
                },
              },
          {
            id: "eliminar",
            etiqueta: "Eliminar",
            icono: <DeleteOutlineIcon />,
            peligro: true,
            visible: puedeEliminar,
            onClick: () => pedirEliminar(row),
          },
        ]}
      />

      <MediumModal
        titulo={`Comprobante de Pago Aguinaldo`}
        isVisible={showPrintModal}
        setVisible={setShowPrintModal}
      >
        <PagoAguinaldoDetails selected={selectedAguinaldo} />
      </MediumModal>

      {dialogo}
    </div>
  );
};

export default Aguinaldo;
